import React, { useState, useEffect } from 'react';
import { Divider, Grid, IconButton, Stack, Typography } from '@mui/material';
import { ArrowBackIos as ArrowBack, ArrowForwardIos as ArrowForward } from '@mui/icons-material';

import Chart from './Chart';
import ChartLegend from './ChartLegend';
import {
  getLegends,
  getIndicatorColorPalette,
  buildConfigChartPoint,
  entityDataExample,
} from '../utils/chartsHelpers';
import { chartLegend } from '../config/settings';
import { printTextSize } from '../utils/entityHelpers';

/**
 * Chart size configuration by screen type
 * @type {Object}
 */
const chartSize = {
  largeScreen: {
    season: {
      width: 680,
      height: 300,
    },
    annual: {
      width: 300,
      height: 250,
    },
  },
  print: {
    season: {
      width: 600,
      height: 230,
    },
    annual: {
      width: 250,
      height: 200,
    },
  },
  mediumScreen: {
    season: {
      width: 500,
      height: 300,
    },
    annual: {
      width: 300,
      height: 250,
    },
  },
  smallScreen: {
    season: {
      width: 230,
      height: 280,
    },
    annual: {
      width: 230,
      height: 280,
    },
  },
};

/**
 * Gets appropriate size for a chart according to parameters
 * @param {string} screenType - Screen type (largeScreen, mediumScreen, smallScreen)
 * @param {boolean} isSeason - If it's a seasonal chart
 * @param {boolean} isPrint - If it's for printing
 * @returns {Object} Object with width and height
 */
const getChartSize = (screenType = 'largeScreen', isSeason = false, isPrint = false) => {
  const seasonType = isSeason ? 'season' : 'annual';

  return isPrint
    ? chartSize.print[seasonType]
    : chartSize[screenType][seasonType] || { width: 200, height: 350 };
};

/**
 * Gets previous item in an array (circular navigation)
 * @param {Array} array - Source array
 * @param {*} currentItem - Current item
 * @returns {*} Previous item or null
 */
const getPreviousArrayItem = (array = [], currentItem = '') => {
  const currentItemIndex = array.indexOf(currentItem);
  if (currentItemIndex === -1) { return null; }
  const previousIndex = currentItemIndex - 1 >= 0 ? currentItemIndex - 1 : array.length - 1;
  return array[previousIndex];
};

/**
 * Gets next item in an array (circular navigation)
 * @param {Array} array - Source array
 * @param {*} currentItem - Current item
 * @returns {*} Next item or null
 */
const getNextArrayItem = (array = [], currentItem = '') => {
  const currentItemIndex = array.indexOf(currentItem);
  if (currentItemIndex === -1) { return null; }
  const nextIndex = currentItemIndex + 1 < array.length ? currentItemIndex + 1 : 0;
  return array[nextIndex];
};

/**
 * ChartContainer - Main container for chart display
 * Manages navigation between seasons, responsive configuration and legend
 *
 * @param {Object} props - Component props
 * @param {boolean} [props.isSingleSeasonRow] - Single season per line display
 * @param {Object} [props.entityData] - Entity data
 * @param {Array} [props.seasons] - Available seasons list
 * @param {string} [props.selectedSeason] - Selected season
 * @param {string} [props.indicatorKey] - Indicator key
 * @param {boolean} [props.isReadingGuide] - Reading guide mode
 * @param {boolean} [props.showLegendHowToRead] - Show "How to read" link
 * @param {string} [props.title] - Chart title
 * @param {string} [props.screenType] - Screen type for responsive
 * @param {boolean} [props.isPrint] - Print mode
 * @param {string} [props.domain] - Territory domain for season tooltips
 * @param {string} [props.altiText] - Altitude text for indicator
 * @returns {React.ReactElement} Chart container with navigation and legend
 */
const ChartContainer = ({
  isSingleSeasonRow = false,
  entityData = entityDataExample,
  seasons = [],
  selectedSeason = '',
  indicatorKey = 'G1',
  isReadingGuide = false,
  showLegendHowToRead = true,
  title = '',
  screenType = 'largeScreen',
  isPrint = false,
  domain,
  altiText = '',
}) => {
  const canSwitchSeason = Boolean(isSingleSeasonRow && selectedSeason);
  const hasSeasons = seasons.length > 1;
  const { width, height } = getChartSize(screenType, hasSeasons, isPrint);

  const [currentSeason, setCurrentSeason] = useState(selectedSeason);

  useEffect(() => {
    setCurrentSeason(selectedSeason);
  }, [selectedSeason]);

  const indicatorColorPalette = getIndicatorColorPalette(indicatorKey);
  const configChartsPoint = buildConfigChartPoint(indicatorColorPalette);
  const legends = getLegends(configChartsPoint, chartLegend.legendItems);

  return (
    <Stack direction="column">
      <Grid
        container
        direction="row"
        justifyContent="center"
        alignItems="center"
        sx={{ mt: isPrint ? 0 : 2, mb: isPrint ? 0 : 1 }}
      >
        {canSwitchSeason && (
          <IconButton
            aria-label="previous"
            onClick={() => setCurrentSeason(getPreviousArrayItem(seasons, currentSeason))}
            sx={{ p: 0 }}
          >
            <ArrowBack />
          </IconButton>
        )}
        <Chart
          indicatorKey={indicatorKey}
          configChartsPoint={configChartsPoint}
          data={entityData}
          selectedSeason={currentSeason}
          isSingleSeasonRow={isSingleSeasonRow}
          width={width}
          height={height}
          title={title}
          isReadingGuide={isReadingGuide}
          isPrint={isPrint}
          screenType={screenType}
          domain={domain}
        />
        {canSwitchSeason && (
          <IconButton
            aria-label="next"
            onClick={() => setCurrentSeason(getNextArrayItem(seasons, currentSeason))}
            sx={{ p: 0 }}
          >
            <ArrowForward />
          </IconButton>
        )}
      </Grid>
      {altiText && (
        <>
          <Divider sx={{ mx: 2 }} />
          <Typography
            variant="body2"
            sx={{ fontWeight: 'bold', my: 1, ...printTextSize }}
          >
            {altiText}
          </Typography>
        </>
      )}
      <ChartLegend
        legendItems={legends}
        isReadingGuide={isReadingGuide}
        showHowToRead={showLegendHowToRead}
        indicatorKey={indicatorKey}
      />
    </Stack>
  );
};

ChartContainer.displayName = 'ChartContainer';

export default ChartContainer;
